import { OFFER_TYPE_HOTEL, OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2, OFFER_TYPE_HOTEL_SLUG, OFFER_TYPE_TOUR_SLUG, OFFER_TYPE_VILLA, OFFER_TYPE_BED_BANK } from 'constants/offer'
import { urlTransform, humanTransform } from 'lib/string/removeSpaceUtils'
import config from 'constants/config'

const placesShortlist = [
  'agra',
  'uae',
  'abu dhabi',
  'spain',
  'south africa',
  'south america',
  'sri lanka',
  'indonesia',
  'switzerland',
  'california',
  'hua hin',
  'bhutan',
  'tunisia',
  'northern territory',
  'turkey',
  'slovenia',
  'ethiopia',
  'tahiti',
  'new caledonia',
  'nepal',
  'russia',
  'gold coast',
  'delhi',
  'scandinavia',
  'myanmar',
  'queensland',
  'scotland',
  'usa',
  'canada',
  'new york',
  'caribbean',
  'mauritius',
  'new south wales',
  'montenegro',
  'khao lak',
  'phillipines',
  'central america',
  'egypt',
  'koh samui',
  'oman',
  'asia',
  'croatia',
  'greece',
  'netherlands',
  'middle east',
  'korea',
  'victoria',
  'czech republic',
  'western australia',
  'cambodia',
  'malta',
  'japan',
  'morocco',
  'china',
  'mexico',
  'act',
  'united kingdom',
  'ireland',
  'singapore',
  'vietnam',
  'seychelles',
  'new zealand',
  'dubai',
  'alaska',
  'poland',
  'fiji',
  'krabi',
  'iceland',
  'portugal',
  'vanuatu',
  'maldives',
  'france',
  'romania',
  'malaysia',
  'south australia',
  'qatar',
  'cook islands',
  'hungary',
  'austria',
  'germany',
  'belgium',
  'south pacific',
  'lombok',
  'india',
  'laos',
  'slovakia',
  'europe',
  'cyprus',
  'bangkok',
  'italy',
  'tasmania',
  'phuket',
  'samoa',
  'hong kong',
  'bali',
  'north america',
  'thailand',
  'hawaii',
  'africa',
  'australia',
  'brazil',
  'goa',
  'americas',
  'philippines',
  'rajasthan',
  'georgia',
  'taiwan',
  'tropical north queensland',
  'armenia',
]

export const landingPageProductNames = [
  'Holiday Packages',
  'Hotels',
  'Tours',
  'Holiday Homes',
]

export const PRODUCT_TYPE_HOTEL = 'Hotels'
export const PRODUCT_TYPE_TOUR = 'Tours'

export const escapeTypes = [
  'Adults-only',
  'All-inclusive',
  'Boutique',
  'City break',
  'Cruises',
  'Family Friendly',
  'Group',
  'Homes & Villas',
  'Honeymoon',
  'Party vibe',
  'Relaxed',
  'Romantic',
  'School holiday',
  'Short Stay',
  'Ski',
  'Spa break',
  'Sun & Beach',
  'Tours',
  'Tours and cruises',
  'Tours by Luxury Escapes',
  'Villa',
]

const escapeTypesWithoutSpace = escapeTypes.map<string>(urlTransform)
const landingPageProductNamesWithoutSpace = landingPageProductNames.map<string>(urlTransform)

function getPageTypeSlug(productName: string) {
  let pageType = OFFER_TYPE_HOTEL_SLUG
  if (productName === 'Tours') {
    pageType = OFFER_TYPE_TOUR_SLUG
  }
  return pageType
}

function calculatePageTypes(productType: string) {
  let pageTypes: Array<App.OfferListOfferType> = []
  if (productType === 'Holiday Homes') {
    pageTypes = [OFFER_TYPE_HOTEL, OFFER_TYPE_VILLA, OFFER_TYPE_BED_BANK]
  } else if (productType === 'Tours') {
    pageTypes = [OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2]
  } else if (productType === 'Holiday Packages') {
    pageTypes = [OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_VILLA, OFFER_TYPE_HOTEL, OFFER_TYPE_BED_BANK]
  } else {
    pageTypes = [OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_VILLA, OFFER_TYPE_HOTEL, OFFER_TYPE_BED_BANK]
  }
  return pageTypes
}

function calculateFilterTypes(productType: string) {
  let filterTypes: Array<string> = []
  if (productType === 'Holiday Homes') {
    filterTypes = ['Homes & Villas']
  }
  return filterTypes
}

function restoreCapitalisation(location: string | undefined, canonicalName?: string | undefined): string | undefined {
  if (canonicalName) {
    return canonicalName
  }
  if (location === undefined) {
    return undefined
  }
  if (location === 'usa') {
    return 'USA'
  }
  if (location == 'act') {
    return 'ACT'
  }
  return location.replace(/\b[a-z]/g, x => x.toUpperCase())
}

function getDestinationProductPage(location: string | undefined, holidayType: string | undefined, productType: string, canonicalName?: string) {
  const pageTypes = calculatePageTypes(productType)
  const filterTypes = calculateFilterTypes(productType)
  const loc = restoreCapitalisation(location, canonicalName)
  let title = (loc ? loc + ' ' : '') + (holidayType ? holidayType + ' ' : '') + productType
  if (productType === 'Hotels' && config.BRAND === 'luxuryescapes') {
    if (!loc) {
      title = 'Luxury Hotels'
    }
    else {
      title = `Find luxury hotels in ${loc.split(',')[0]}`
    }
  }

  return {
    title,
    places: loc ? [loc] : [],
    holidayTypes: filterTypes,
    pageTypes,
  }
}

function getSubheading(location: string | undefined, productType: string, canonicalName?: string): string {
  const loc = restoreCapitalisation(location, canonicalName)
  let subheading = `Explore our ${productType.replace(/s$/, '').replace(/s /, '')} deals`
  if (config.BRAND === 'luxuryescapes' && productType === 'Hotels') {
    subheading = 'Escape with our luxury hotel deals'
  }
  if (loc) {
    subheading += ` in ${loc}`
  }
  return subheading
}

function getDescription(location: string | undefined, holidayType: string | undefined, productType: string, canonicalName?: string) {
  const loc = restoreCapitalisation(location, canonicalName)
  if (productType === 'Holiday Packages') {
    const year = new Date().getFullYear()
    return (loc ? loc + ' ' : '') + (holidayType ? holidayType + ' ' : '') + 'Holiday Package deals for travel in ' + year + '/' + (year + 1) + '. Save on luxury hotel + flight bundles. Get exclusive inclusions.'
  }
  if (productType === 'Hotels') {
    return 'Book the most exclusive boutique, luxury and 5 star accommodation' + (loc ? ' in ' + loc : '') + ' at the best prices.'
  }
  let metaDescription = 'Find the latest deals & exclusive special offers for '
  if (holidayType) {
    metaDescription += holidayType + ' '
  }
  metaDescription += productType
  if (loc) {
    metaDescription += ' in ' + loc
  }
  metaDescription += `. ${config.head.metaTitleForDestinationProductPage}.`
  return metaDescription
}

function getLuxHotelMetaDescription(productType: string, location?: string, canonicalName?: string): string {
  const loc = restoreCapitalisation(location, canonicalName)

  const locations = loc?.split(',')
  const smallestGeoArea = locations?.[0] || ''
  const country = locations?.[locations.length - 1] || ''
  let locationDescription = 'Exclusive deals on luxury hotels & resorts'
  if (smallestGeoArea || country) {
    locationDescription = `Exclusive deals on luxury hotels & resorts in ${smallestGeoArea},${country}`
  }
  return `${locationDescription}. Enjoy 5-star accommodation at the best prices only with ${config.title}.`
}

function getMetaHeadTitle(regionCode: string, location: string | undefined, holidayType: string | undefined, productType: string, canonicalName?: string, isExperiment?: boolean) {
  if (isExperiment) {
    return getExperimentHolidayPackageHeadDataTitle(productType, holidayType, location, canonicalName)
  }

  const isLuxHotel = isLuxuryEscapesHotel(productType)
  if (isLuxHotel) {
    return getLuxHotelHeadDataTitle(productType, location, canonicalName)
  }
  else {
    return getHeadTitle(location, holidayType, productType, canonicalName) + ' - ' + config.title + ' ' + regionCode
  }
}

function getMetaDescription(location: string | undefined, holidayType: string | undefined, productType: string, canonicalName?: string, isExperiment?: boolean) {
  if (isExperiment) {
    return getExperimentHolidayPackageMetaDescription(productType, location, canonicalName)
  }

  const isLuxHotel = isLuxuryEscapesHotel(productType)
  if (isLuxHotel) {
    return getLuxHotelMetaDescription(productType, location, canonicalName)
  }
  return getDescription(location, holidayType, productType, canonicalName)
}

function getHeadTitle(location: string | undefined, holidayType: string | undefined, productType: string, canonicalName?: string) {
  const loc = restoreCapitalisation(location, canonicalName)
  if (productType === 'Holiday Packages') {
    const year = new Date().getFullYear()
    return (loc ? loc + ' ' : '') + (holidayType ? holidayType + ' ' : '') + 'Holidays & Package Deals ' + year + '/' + (year + 1)
  }
  if (productType === 'Hotels') {
    if (config.BRAND === 'luxuryescapes') {
      return 'Best Luxury and 5 Star Hotels and Resorts' + (loc ? ' in ' + loc : '')
    }
    return '4 & 5 Star ' + (holidayType ? holidayType + ' ' : '') + 'Hotels' + (loc ? ' in ' + loc : '')
  }
  return getTitle(location, holidayType, productType, canonicalName)
}

function getSmallestGeoLocation(location: string | undefined, canonicalName?: string) {
  const loc = restoreCapitalisation(location, canonicalName)
  const smallestGeoArea = loc?.split(',')[0] || ''
  return smallestGeoArea
}

function getLuxHotelHeadDataTitle(productType: string, location?: string, canonicalName?: string): string {
  const smallestGeoArea = getSmallestGeoLocation(location, canonicalName)
  const locationDescription = smallestGeoArea ? `${smallestGeoArea} Luxury and 5 Star Hotels and Resorts` : 'Luxury and 5 Star Hotels and Resorts'
  return `${locationDescription} - ${config.title}`
}

function getExperimentHolidayPackageHeadDataTitle(productType: string, holidayType?: string, location?: string, canonicalName?: string): string {
  const smallestGeoArea = getSmallestGeoLocation(location, canonicalName)
  const year = new Date().getFullYear()
  const description = (smallestGeoArea ? smallestGeoArea + ' ' : '') + (holidayType ? holidayType + ' ' : '') + 'Holidays Packages & Deals ' + year + '/' + (year + 1)
  return `${description} - ${config.title}`
}

function getExperimentHolidayPackageMetaDescription(productType: string, location?: string, canonicalName?: string): string {
  const loc = restoreCapitalisation(location, canonicalName)

  const locations = loc?.split(',')
  const smallestGeoArea = locations?.[0] || ''
  const country = locations?.[locations.length - 1] || ''
  let locationDescription = 'Exclusive deals on luxury hotels & resorts'
  if (smallestGeoArea || country) {
    locationDescription = `${smallestGeoArea},${country}`
  }
  return `Discover the best ${locationDescription} holiday package deals. Save on luxury hotel + flight packages, with exclusive inclusions only at ${config.title}.`
}

function getTitle(location: string | undefined, holidayType: string | undefined, productType: string, canonicalName?: string) {
  return getDestinationProductPage(location, holidayType, productType, canonicalName)?.title
}

function getRelatedTravelItems(location: string | undefined, holidayType: string | undefined, productType: string, canonicalName?: string) {
  const loc = restoreCapitalisation(location, canonicalName)
  return landingPageProductNames.map(productName => {
    if (!holidayType && productName === productType) {
      return null
    }
    if (productName !== 'Hotels' && location && !placesShortlist.includes(location)) {
      return null
    }
    return {
      text: `${loc ? loc + ' ' : ''}${holidayType ? holidayType + ' ' : ''}${productName}`,
      url: {
        to: `/${loc ? urlTransform(loc).toLowerCase() + '.' : ''}${holidayType ? urlTransform(holidayType).toLowerCase() + '.' : ''}${urlTransform(productName).toLowerCase()}`,
      },
    }
  }).filter(item => item)
}

function isNotLowerCase(s: string): boolean {
  return s.toLowerCase() !== s
}

// For legacy reasons, this function returns a lower case location, and an upper cased holiday type and product type.
// If a redirect due to incorrect case is needed, shouldRedirect is set to true.
// Elsewhere in this file, capitalisation of the location is achieved using the restoreCapitalisation function.
function decodeUrlParams(params: { locationOrHolidayType?: string, location?: string, holidayType?: string, productType: string, city?: string, state?: string, country?: string }) {
  const { locationOrHolidayType, location, holidayType, productType, city, state, country } = params

  let holidayType_: string | undefined = undefined
  if (holidayType || locationOrHolidayType) {
    holidayType_ = escapeTypes[escapeTypesWithoutSpace.findIndex((e) => e.toLowerCase() === (holidayType ?? locationOrHolidayType ?? '').toLowerCase())]
  }

  let location_: string | undefined = humanTransform([location, city, state, country].filter(Boolean).join(','))

  location_ = location_ !== '' ? location_ : undefined

  if (!location_ && !!locationOrHolidayType && !holidayType_) {
    location_ = humanTransform(locationOrHolidayType)
  }

  const productType_ = landingPageProductNames[landingPageProductNamesWithoutSpace.findIndex((e) => e.toLowerCase() === productType.toLowerCase())]

  const shouldRedirect =
    isNotLowerCase(productType) ||
    (!!location_ && isNotLowerCase(location_)) ||
    (!!holidayType_ && isNotLowerCase(holidayType ?? locationOrHolidayType ?? '')) ||
    (productType_ === 'Hotels' && !country)

  if (productType_ !== 'Hotels' && !shouldRedirect) {
    const locationInnerLevel = location_?.split(', ')[0] ?? ''
    location_ = placesShortlist.includes(locationInnerLevel) ? location_ : undefined
  }

  return { location: location_, holidayType: holidayType_, productType: productType_, shouldRedirect }
}

// roll out SEO testing results into all the luxury Escapes  destination hotel pages
// Following logic will be used to determine if the page is related to the lux hotel
function isLuxuryEscapesHotel(productType: string): boolean {
  if (productType === 'Hotels') {
    if (config.BRAND === 'luxuryescapes') {
      return true
    }
  }
  return false
}

// SEO experiment for .holidaypackages pages
// following logic will be used to determine if the holidaypackages experiment is on
function isExperimentOn(url: string, productType: string): boolean {
  if (productType === 'Holiday Packages') {
    if (config.BRAND === 'luxuryescapes') {
      return url.length % 2 === 1
    }
  }
  return false
}

export { getDestinationProductPage, getDescription, getHeadTitle, getTitle, getRelatedTravelItems, getPageTypeSlug, decodeUrlParams, calculatePageTypes, getSubheading, restoreCapitalisation, getLuxHotelHeadDataTitle, getLuxHotelMetaDescription, isLuxuryEscapesHotel, getMetaHeadTitle, getMetaDescription, isExperimentOn }
